import React, { Component } from 'react';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Divider, Grid, Form, Image, Card, Icon } from 'semantic-ui-react';

import TweetEmbed from 'react-tweet-embed';
import DyzioModal from 'dyzio-shared-components/lib/components/dyzio-modal';
import YoutubeEmbed from 'react-youtube';
import DatePicker from 'react-datepicker';

import { compose } from 'recompose';
import hasRESTClient from '../../hocs/has-rest-client';
import hasJSONAPIClient from '../../hocs/has-jsonapi-client';

import FacebookEmbed from '../../components/facebook-embed';
import DyzioInstagramEmbed from '../../components/dyzio-instagram-embed';
import Uploader from '../../components/uploader';
import TargetLink from '../../components/target-link';
import AssetTypeIcon from '../../components/asset-type-icon';
import { SUPPORTING_FILE } from '../../utils/static-configuration';

const DEFAULT_IMAGE = 'https://s3-eu-west-1.amazonaws.com/dyzio-static-files/images/instagram-story2.png';

const breakWordStyle = {
  wordBreak: 'break-word',
};

const centreImgStyle = {
  margin: 'auto',
};

const EmbedWrapper = ({ asset }) => {
  const youtubeEmbedOpts = {
    color: 'white',
    width: '100%',
    showinfo: 1,
  };

  const EMBEDS = {
    INSTAGRAM: <DyzioInstagramEmbed assetId={asset.assetId} />,
    TWITTER: <TweetEmbed id={asset.assetId} />,
    FACEBOOK: <FacebookEmbed url={asset.assetURL} size="small" />,
    YOUTUBE: <YoutubeEmbed videoId={asset.assetId} opts={youtubeEmbedOpts} />,
    SNAPCHAT: <div>SNAPCHAT asset</div>,
    VK: <div>VK asset</div>,
    BLOG: <div>Blog asset</div>,
  };

  return EMBEDS[asset.platform];
};

const INITIAL_STATE = {
  open: false,
  uploaderState: 1,
  documentData: {},
  success: false,
  reach: null,
  impressions: null,
  engagements: null,
  publishedDate: null,
  clicks: null,
  actions: null,
  supportingDataId: null,
  savedOK: false,
  shouldUpdatePublishedDate: false,
  callbackData: null,
};

class UploadModal extends Component {
  constructor(props) {
    super(props);
    const { asset: { publishedDate } } = props;
    this.state = {
      ...INITIAL_STATE,
      publishedDate,
    };
    this.updateTrackedAssetPublishedDate();
  }

  show = size => () => this.setState({ size, open: true })

  close = () => this.setState({ open: false })

  updateUploaderState = newUploaderState => {
    this.setState({ uploaderState: newUploaderState });
  }

  uploadDataCallback = documentData => {
    this.setState({ documentData, success: true });
  }

  saveSupportingData = () => {
    const { supportingDataId } = this.state;
    this.setState({ savedOK: false });
    if (supportingDataId) {
      return this.patchSupportingData(supportingDataId);
    }
    return this.createSupportingData();
  }

  createSupportingData = () => {
    const { documentData, reach, impressions, engagements, actions, clicks, likes, comments, views, shares, saves, stickerTaps } = this.state;
    const { asset, successHandler, restClient, jsonAPIClient } = this.props;

    // create supportingData object
    const supportingData = jsonAPIClient.create('supportingData');
    supportingData.set('status', 'CREATED');
    supportingData.set('asset', {
      type: 'trackedAssets',
      id: asset.id,
    });

    supportingData.set('assetId', asset.id);
    supportingData.set('assetUrl', asset.assetURL);
    supportingData.set('assetPlatform', asset.platform);

    if (documentData && documentData.filePath) {
      supportingData.set('filePath', documentData.filePath);
    } else {
      supportingData.set('filePath', 'no_image_available.png');
    }

    if (views) {
      supportingData.set('views', views);
    }

    if (shares) {
      supportingData.set('shares', shares);
    }

    if (saves) {
      supportingData.set('saves', saves);
    }

    if (stickerTaps) {
      supportingData.set('stickerTaps', stickerTaps);
    }

    if (likes) {
      supportingData.set('likes', likes);
    }

    if (comments) {
      supportingData.set('comments', comments);
    }

    if (reach) {
      supportingData.set('reach', reach);
    }

    if (impressions) {
      supportingData.set('impressions', impressions);
    }

    if (engagements) {
      supportingData.set('engagements', engagements);
    }

    if (actions) {
      supportingData.set('actions', actions);
    }

    if (clicks) {
      supportingData.set('clicks', clicks);
    }

    let cbData = null;
    return supportingData.sync()
      .then(sd => {
        const sdJson = sd.toJSON();

        cbData = sdJson;

        this.setState({
          supportingDataId: sdJson.id,
          callbackData: cbData,
        });

        return restClient.post(`/${asset.type}/${asset.id}/relationships/supportingData/`, {
          data: {
            type: 'supportingData',
            id: sdJson.id,
          },
        })
          .then(r => {
            console.log(r);
            console.log('ASSET UPDATED.');
            this.setState({
              savedOK: true,
            }, () => successHandler(cbData));
            return r;
          });
      });
  }

  patchSupportingData = supportingDataId => {
    const { documentData, reach, impressions, engagements, actions, clicks, likes, comments, views, shares, saves, stickerTaps } = this.state;
    const {
      successHandler,
      restClient,
    } = this.props;

    const patch = {
      reach,
      impressions,
      engagements,
      actions,
      clicks,
      likes,
      views,
      comments,
      shares,
      saves,
      stickerTaps,
      filePath: documentData.filePath,
    };

    const safePatch = _.omitBy(patch, _.isNil);

    restClient.patch(`/supportingData/${supportingDataId}`, {
      data: {
        type: 'supportingData',
        id: supportingDataId,
        attributes: safePatch,
      },
    })
      .then(sd => {
        const sdJson = sd.data.data.attributes;
        const cbData = sdJson;
        successHandler(cbData);
        this.setState({
          supportingDataId: sdJson.id,
          callbackData: cbData,
          savedOK: true,
        });
      });
  }

  handleFormFieldChange = (e, { name, value }) => this.setState({ savedOK: false, [name]: value })

  toggler = () => {
    const { popupToggleHandler } = this.props;
    this.setState(INITIAL_STATE);
    popupToggleHandler();
  }

  updateTrackedAssetPublishedDate = () => {
    const { publishedDate } = this.state;
    const { asset: { id, type } } = this.props;
    const { jsonAPIClient } = this.props;
    return jsonAPIClient.get(type, id).then(response => {
      response.set('publishedDate', publishedDate);
      return response.sync();
    });
  }

  onPublishedDateChanged = date => {
    this.setState({
      publishedDate: moment(date).set('hour', 15).format(),
    }, () => this.updateTrackedAssetPublishedDate());
  }

  render() {
    const {
      uploaderState,
      savedOK,
      documentData,
      reach,
      views,
      impressions,
      engagements,
      clicks,
      actions,
      likes,
      comments,
      publishedDate,
      shares,
      saves,
      stickerTaps,
    } = this.state;

    const { asset, popupToggleHandler } = this.props;

    const isVK = _.get(asset, 'platform') === 'VK';
    const isBlog = _.get(asset, 'platform') === 'BLOG';
    const isSnapchat = _.get(asset, 'platform') === 'SNAPCHAT';
    const isStory = _.get(asset, 'assetType', '') === 'STORY';
    const isManual = _.get(asset, 'createdSource', '') === 'CAMPAIGN';
    const assetLink = _.get(asset, 'assetURL');
    const assetText = _.get(asset, 'extraData.text', '-');

    const datePickerValue = publishedDate ? moment(publishedDate) : '';

    const isManualStory = isStory && isManual;

    const reachOrImpressionsAdded = true; // Boolean(reach) || Boolean(impressions);

    return (
      <DyzioModal
        onClose={popupToggleHandler}
        headerText="Add data and/or Media"
        size="large"
        hasActionButton
        actionButtonContent={savedOK ? 'close' : 'save'}
        actionButtonColor={savedOK ? 'green' : 'pink'}
        actionButtonIcon={<Icon name={savedOK ? 'checkmark' : 'save'} />}
        onActionButtonClick={savedOK ? this.toggler : this.saveSupportingData}
        shouldDisableActionButton={!reachOrImpressionsAdded}
      >
        <Grid columns={3} divided>
          <Grid.Row>
            <Grid.Column>
              <h4>Asset </h4>
              <Divider />

              <Card fluid>
                <Card.Content>
                  <AssetTypeIcon asset={asset} renderLabelAsText />
                </Card.Content>
                <Card.Content>
                  { assetText }
                </Card.Content>
              </Card>

              <Card fluid>
                <Card.Content>Published date</Card.Content>
                <Card.Content>
                  { moment(publishedDate).format('lll') }
                </Card.Content>
              </Card>

              {
                !isManualStory && !isSnapchat &&
                <Card fluid>
                  <Card.Content>Link to blog or social post</Card.Content>
                  <Card.Content>
                    <p style={breakWordStyle}>
                      <TargetLink renderIf href={assetLink}>
                        {_.truncate(assetLink, { length: 300 })}
                      </TargetLink>
                    </p>
                  </Card.Content>
                </Card>
              }

            </Grid.Column>
            <Grid.Column>
              <h4>Media (optional)</h4>
              <Divider />
              {
                documentData.filePath
                  ? <Image
                    style={centreImgStyle}
                    src={`https://s3-eu-west-1.amazonaws.com/discussions-uploads-production/${documentData.filePath}`}
                    onError={i => { i.target.src = DEFAULT_IMAGE; }}
                  />
                  :
                  <Uploader
                    uploaderState={uploaderState}
                    updateUploaderState={this.updateUploaderState}
                    docType={SUPPORTING_FILE}
                    uploadDataCallback={this.uploadDataCallback}
                  />
              }
            </Grid.Column>
            <Grid.Column textAlign="left">
              <h4 style={{ textAlign: 'center' }}>Data</h4>
              <Divider />
              {
                isBlog && (
                  <Form>
                    <Form.Field>
                      <Form.Input
                        name="views"
                        label="views"
                        placeholder="views"
                        type="views"
                        value={views}
                        onChange={this.handleFormFieldChange}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        name="comments"
                        label="comments"
                        placeholder="comments"
                        type="comments"
                        value={comments}
                        onChange={this.handleFormFieldChange}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        name="likes"
                        label="likes"
                        placeholder="likes"
                        type="likes"
                        value={likes}
                        onChange={this.handleFormFieldChange}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        name="clicks"
                        label="clicks"
                        placeholder="clicks"
                        type="clicks"
                        value={clicks}
                        onChange={this.handleFormFieldChange}
                      />
                    </Form.Field>
                  </Form>
                )
              }
              {
                isVK && (
                  <Form>
                    <Form.Field>
                      <Form.Input
                        name="views"
                        label="views"
                        placeholder="views"
                        type="views"
                        value={views}
                        onChange={this.handleFormFieldChange}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        name="likes"
                        label="likes"
                        placeholder="likes"
                        type="likes"
                        value={likes}
                        onChange={this.handleFormFieldChange}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        name="comments"
                        label="comments"
                        placeholder="comments"
                        type="comments"
                        value={comments}
                        onChange={this.handleFormFieldChange}
                      />
                    </Form.Field>
                  </Form>
                )
              }
              {
                isSnapchat && (
                  <Form>
                    <Form.Field>
                      <Form.Input
                        name="views"
                        label="views"
                        placeholder="views"
                        type="views"
                        value={views}
                        onChange={this.handleFormFieldChange}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        name="shares"
                        label="shares"
                        placeholder="shares"
                        type="shares"
                        value={shares}
                        onChange={this.handleFormFieldChange}
                      />
                    </Form.Field>
                  </Form>
                )
              }
              {
                !isBlog && !isVK && !isSnapchat &&
                <Form>
                  <Form.Field>
                    <Form.Field>
                      <Form.Input
                        name="views"
                        label="views"
                        placeholder="views"
                        type="views"
                        value={views}
                        onChange={this.handleFormFieldChange}
                      />
                    </Form.Field>
                    <Form.Input
                      name="impressions"
                      label="impressions"
                      placeholder="impressions"
                      type="impressions"
                      value={impressions}
                      onChange={this.handleFormFieldChange}
                    />
                  </Form.Field>
                  <Form.Field>
                    <Form.Input
                      name="reach"
                      label="reach"
                      placeholder="reach"
                      type="reach"
                      value={reach}
                      onChange={this.handleFormFieldChange}
                    />
                  </Form.Field>
                  <Form.Field>
                    <Form.Input
                      name="clicks"
                      label="clicks"
                      placeholder="clicks"
                      type="clicks"
                      value={clicks}
                      onChange={this.handleFormFieldChange}
                    />
                  </Form.Field>
                  <Form.Field>
                    <Form.Input
                      name="shares"
                      label="shares"
                      placeholder="shares"
                      type="shares"
                      value={shares}
                      onChange={this.handleFormFieldChange}
                    />
                  </Form.Field>
                  <Form.Field>
                    <Form.Input
                      name="saves"
                      label="saves"
                      placeholder="saves"
                      type="saves"
                      value={saves}
                      onChange={this.handleFormFieldChange}
                    />
                  </Form.Field>
                  <Form.Field>
                    <Form.Input
                      name="stickerTaps"
                      label="sticker taps"
                      placeholder="stickerTaps"
                      type="stickerTaps"
                      value={stickerTaps}
                      onChange={this.handleFormFieldChange}
                    />
                  </Form.Field>
                </Form>
              }
              {
                savedOK &&
                <div style={{ textAlign: 'center', color: 'pink' }}>
                  <Divider />
                  <b>Saved OK!</b>
                </div>
              }
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </DyzioModal>
    );
  }
}

UploadModal.propTypes = {
  asset: PropTypes.object.isRequired,
  popupToggleHandler: PropTypes.object.isRequired,
  successHandler: PropTypes.object.isRequired,
  jsonAPIClient: PropTypes.object.isRequired,
  restClient: PropTypes.func.isRequired,
};

export default compose(
  hasRESTClient,
  hasJSONAPIClient,
)(UploadModal);



/** WEBPACK FOOTER **
 ** ./src/containers/upload-modal/index.js
 **/